import { createResolverRegistry } from '../resolvers/resolverRegistry';
import { toLoading } from '../resolvers/definition/resolverState';
import { createEntityLifecycleManager } from '../lifecycleManager/entityLifecycleManager';
import { create } from 'event-emitter';
import { lifecycleRules } from '../resolvers/decorators/lifecycleRules';
export const createController = ({
  httpClient,
  globalDecorators = [],
  createDevtools
}) => {
  const stateEmitter = create();
  const entityDefinitions = new Map();
  const registerEntityDef = entity => {
    entityDefinitions.set(entity.typeName, entity);
  };
  const resolverRegistry = createResolverRegistry({
    httpClient,
    globalDecorators: [lifecycleRules, ...globalDecorators]
  });
  const lifecycleManager = createEntityLifecycleManager({
    onStateChange: (__entity, instanceKey, state) => stateEmitter.emit(instanceKey, state),
    onInvalidate: (entity, instanceKey) => {
      if (resolverRegistry.isAttached({
        instanceKey,
        entity
      })) {
        resolverRegistry.refresh({
          instanceKey,
          entity
        });
      } else {
        // No resolver attached, manually mark this entity as stale for next attachment
        lifecycleManager.markStale(entity, instanceKey);
      }
    }
  });
  createDevtools === null || createDevtools === void 0 || createDevtools({
    entityDefinitions,
    lifecycleManager,
    resolverRegistry
  });
  const watch = ({
    entity,
    params = {},
    instanceKey = entity.identifyInstance(params)
  }, callback) => {
    registerEntityDef(entity);

    // Attach if first subscriber
    if (stateEmitter.count(instanceKey) === 0) {
      const emptyInitial = toLoading()();
      const getState = () => lifecycleManager.getState(entity, instanceKey);
      resolverRegistry.attach({
        entity,
        instanceKey,
        params,
        getState: () => getState() || emptyInitial,
        onResolverUpdate: resolverState => lifecycleManager.handleStateChange(entity, instanceKey, resolverState)
      });
      const state = getState();
      if (!state || state.stale || state.loading) {
        resolverRegistry.refresh({
          instanceKey,
          entity
        });
      }
    }
    const off = stateEmitter.on(instanceKey, callback !== null && callback !== void 0 ? callback : () => {});
    return () => {
      // Detach if last subscriber
      if (off() === 0) {
        resolverRegistry.detach({
          instanceKey,
          entity
        });
        // TODO: Queue for cache GC
      }
    };
  };
  return {
    watch,
    invalidate: ({
      entity,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      return lifecycleManager.invalidate(entity, instanceKey);
    },
    getState: ({
      entity,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      return lifecycleManager.getState(entity, instanceKey);
    },
    setState: ({
      entity,
      state,
      params = {},
      instanceKey = entity.identifyInstance(params)
    }) => {
      registerEntityDef(entity);
      const nextState = typeof state === 'function' ? state(lifecycleManager.getState(entity, instanceKey)) : state;
      return lifecycleManager.handleStateChange(entity, instanceKey, nextState);
    }
  };
};